import { useState, useEffect, useCallback } from "react";
import { axiosInstance } from "../App";
import { useBox } from "../components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { printHtmlInWindow } from "../utils/printHelper";
import { Ticket, Trophy, ClipboardCheck, History, Settings, Trash2, Plus, Pencil, DollarSign, PartyPopper, BarChart3, Printer, Radio } from "lucide-react";
import { LiveDrawResults } from "../components/LiveDrawResults";
import { PublicResultsLink } from "../components/PublicResultsLink";

const fmtCRC = (n) => `₡${Math.round(n || 0).toLocaleString("es-CR")}`;
const todayCR = () => {
  const now = new Date();
  const cr = new Date(now.toLocaleString("en-US", { timeZone: "America/Costa_Rica" }));
  return `${cr.getFullYear()}-${String(cr.getMonth() + 1).padStart(2, "0")}-${String(cr.getDate()).padStart(2, "0")}`;
};
const nowCRminutes = () => {
  const cr = new Date(new Date().toLocaleString("en-US", { timeZone: "America/Costa_Rica" }));
  return cr.getHours() * 60 + cr.getMinutes();
};
// Cierre 2 minutos antes de la hora TICA (solo para el sorteo de hoy)
const isDrawClosed = (draw, date) => {
  if (!draw?.draw_time || !draw.draw_time.includes(":")) return false;
  if (date !== todayCR()) return false;
  const [hh, mm] = draw.draw_time.split(":");
  const cutoff = parseInt(hh, 10) * 60 + parseInt(mm, 10) - 2;
  return nowCRminutes() >= cutoff;
};
const buildTicketHtml = (bet, draw, boxName) => {
  const mult = bet.is_reventado && draw?.payout_reventado ? draw.payout_reventado : (draw?.payout_normal || 0);
  const potential = (bet.amount || 0) * mult;
  const when = new Date(bet.created_at).toLocaleString("es-CR", { timeZone: "America/Costa_Rica" });
  return `<html><head><title>Tiquete</title><style>
    @page { size: 58mm auto; margin: 0; }
    * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
    body { margin:0; padding:3mm; font-family: 'Courier New', monospace; color:#000; width:58mm; box-sizing:border-box; }
    .c{ text-align:center; } .b{ font-weight:bold; }
    .big{ font-size:22pt; font-weight:bold; letter-spacing:2px; }
    hr{ border:none; border-top:1px dashed #000; margin:2mm 0; }
    .row{ display:flex; justify-content:space-between; font-size:9pt; }
    .sm{ font-size:8pt; }
  </style></head><body>
    <div class="c b">${boxName || "PaliStore"}</div>
    <div class="c sm">TIQUETE DE APUESTA</div>
    <hr/>
    <div class="c b">${draw?.name || bet.draw_name || ""}</div>
    <div class="c sm">${draw?.country || bet.draw_country || ""}${draw?.draw_time ? " · " + draw.draw_time : ""}</div>
    <div class="c big">${bet.number}${bet.is_reventado ? " R" : ""}</div>
    <hr/>
    <div class="row"><span>Jugador:</span><span class="b">${bet.player_name}</span></div>
    <div class="row"><span>Monto:</span><span class="b">${fmtCRC(bet.amount)}</span></div>
    <div class="row"><span>Paga:</span><span>x${mult}</span></div>
    <div class="row"><span>Premio:</span><span class="b">${fmtCRC(potential)}</span></div>
    ${bet.serie ? `<div class="row"><span>Serie:</span><span>${bet.serie}</span></div>` : ""}
    <hr/>
    <div class="sm">Fecha sorteo: ${bet.draw_date}</div>
    <div class="sm">Registrado: ${when}</div>
    <div class="sm">Tiquete: ${(bet.id || "").slice(0, 8)}</div>
    <hr/>
    <div class="c sm">¡Mucha suerte!</div>
  </body></html>`;
};
const printTicket = (bet, draw, boxName) => printHtmlInWindow(buildTicketHtml(bet, draw, boxName), { width: 320, height: 560 });

export default function Apuestas({ user }) {
  const { selectedBox } = useBox();
  const isMaster = user?.role === "master";
  const isSuper = !!user?.is_super_master;

  const [activeTab, setActiveTab] = useState("register");
  const [draws, setDraws] = useState([]);
  const [register, setRegister] = useState(null);

  const fetchDraws = useCallback(async () => {
    try {
      const r = await axiosInstance.get(`/lottery/draws`);
      setDraws(r.data);
    } catch (e) { /* noop */ }
  }, []);

  const fetchRegister = useCallback(async () => {
    if (!selectedBox) return;
    try {
      const r = await axiosInstance.get(`/cash-register/current?box_id=${selectedBox.id}`);
      setRegister(r.data && r.data.id && r.data.status !== "cerrada" ? r.data : null);
    } catch (e) { setRegister(null); }
  }, [selectedBox]);

  useEffect(() => { fetchDraws(); fetchRegister(); }, [fetchDraws, fetchRegister]);

  if (!selectedBox) {
    return (
      <div className="p-8 text-center text-slate-500" data-testid="apuestas-no-box">
        Selecciona una caja para usar el módulo de Apuestas y Sorteos.
      </div>
    );
  }

  if (!isSuper && !selectedBox.lottery_accessible) {
    return (
      <div className="p-8 text-center text-slate-500" data-testid="apuestas-no-access">
        No tiene acceso al módulo de Apuestas y Sorteos para esta caja. Contacte al administrador.
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="apuestas-page">
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-amber-100 text-amber-700"><Ticket className="w-6 h-6" /></div>
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: "Space Grotesk, sans-serif" }}>Apuestas y Sorteos</h1>
          <p className="text-sm text-slate-500">Caja: {selectedBox.name} · {register ? <span className="text-green-600 font-medium">Turno abierto</span> : <span className="text-red-500 font-medium">Sin turno abierto</span>}</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="register" data-testid="tab-register"><Ticket className="w-4 h-4 mr-1" /> Registrar</TabsTrigger>
          <TabsTrigger value="live" data-testid="tab-live"><Radio className="w-4 h-4 mr-1" /> Resultados de Sorteos</TabsTrigger>
          <TabsTrigger value="prizes" data-testid="tab-prizes"><Trophy className="w-4 h-4 mr-1" /> Premios</TabsTrigger>
          {isMaster && <TabsTrigger value="results" data-testid="tab-results"><ClipboardCheck className="w-4 h-4 mr-1" /> Resultados</TabsTrigger>}
          <TabsTrigger value="history" data-testid="tab-history"><History className="w-4 h-4 mr-1" /> Historial</TabsTrigger>
          <TabsTrigger value="report" data-testid="tab-report"><BarChart3 className="w-4 h-4 mr-1" /> Reporte</TabsTrigger>
          {isSuper && <TabsTrigger value="draws" data-testid="tab-draws"><Settings className="w-4 h-4 mr-1" /> Sorteos</TabsTrigger>}
        </TabsList>

        <TabsContent value="register" className="mt-4">
          <RegisterBet box={selectedBox} draws={draws} register={register} onRegistered={fetchRegister} />
        </TabsContent>
        <TabsContent value="live" className="mt-4">
          <LiveDrawResults headerExtra={user?.role === "master" ? <PublicResultsLink isSuperMaster={!!user?.is_super_master} /> : null} />
        </TabsContent>
        <TabsContent value="prizes" className="mt-4">
          <PrizesTab box={selectedBox} register={register} isMaster={isMaster} />
        </TabsContent>
        {isMaster && (
          <TabsContent value="results" className="mt-4">
            <ResultsTab box={selectedBox} draws={draws} />
          </TabsContent>
        )}
        <TabsContent value="history" className="mt-4">
          <HistoryTab box={selectedBox} draws={draws} />
        </TabsContent>
        <TabsContent value="report" className="mt-4">
          <ReportTab box={selectedBox} />
        </TabsContent>
        {isSuper && (
          <TabsContent value="draws" className="mt-4">
            <DrawsConfig draws={draws} onChange={fetchDraws} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

function RegisterBet({ box, draws, register, onRegistered }) {
  const activeDraws = draws.filter((d) => d.active);
  const [drawId, setDrawId] = useState("");
  const [playerName, setPlayerName] = useState("");
  const [customerCode, setCustomerCode] = useState("");
  const [number, setNumber] = useState("");
  const [serie, setSerie] = useState("");
  const [isReventado, setIsReventado] = useState(false);
  const [amount, setAmount] = useState("");
  const [saving, setSaving] = useState(false);
  const [todayBets, setTodayBets] = useState([]);
  const [deleteId, setDeleteId] = useState(null);

  const draw = draws.find((d) => d.id === drawId);
  const digits = draw ? draw.digits : 2;

  const formatNumbers = (raw) => {
    const d = (raw || "").replace(/\D/g, "");
    const groups = [];
    for (let i = 0; i < d.length; i += digits) groups.push(d.slice(i, i + digits));
    return groups.join("+");
  };
  const parsedNumbers = number.split("+").map((s) => s.trim()).filter((s) => s.length === digits);
  const betCount = parsedNumbers.length;

  const fetchToday = useCallback(async () => {
    try {
      const r = await axiosInstance.get(`/lottery/bets?box_id=${box.id}&date=${todayCR()}`);
      setTodayBets(r.data);
    } catch (e) { /* noop */ }
  }, [box.id]);

  useEffect(() => { fetchToday(); }, [fetchToday]);

  const lookupCustomer = async () => {
    if (!customerCode.trim()) return;
    try {
      const r = await axiosInstance.get(`/customers/by-code/${customerCode.trim().padStart(4, "0")}`);
      if (r.data && r.data.name) { setPlayerName(r.data.name); toast.success(`Cliente: ${r.data.name}`); }
    } catch (e) { toast.error("Cliente no encontrado"); }
  };

  const prizePreview = draw && amount ? Number(amount) * (isReventado && draw.payout_reventado ? draw.payout_reventado : draw.payout_normal) : 0;
  const totalBet = amount ? Number(amount) * Math.max(1, betCount) : 0;

  const submit = async () => {
    if (!drawId) { toast.error("Selecciona un sorteo"); return; }
    if (!playerName.trim()) { toast.error("Nombre del jugador requerido"); return; }
    if (parsedNumbers.length === 0) { toast.error(`Ingresa al menos un número de ${digits} dígitos`); return; }
    if (!amount || Number(amount) <= 0) { toast.error("Monto inválido"); return; }
    setSaving(true);
    try {
      await axiosInstance.post(`/lottery/bets`, {
        box_id: box.id,
        cash_register_id: register?.id || null,
        draw_id: drawId,
        player_name: playerName.trim(),
        customer_code: customerCode.trim() || null,
        number: parsedNumbers[0],
        numbers: parsedNumbers,
        serie: serie.trim() || null,
        is_reventado: isReventado,
        amount: Number(amount),
      });
      toast.success(parsedNumbers.length > 1 ? `${parsedNumbers.length} apuestas registradas` : "Apuesta registrada");
      setNumber(""); setAmount(""); setSerie(""); setIsReventado(false);
      fetchToday();
      onRegistered?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al registrar apuesta");
    } finally { setSaving(false); }
  };

  const doDelete = async () => {
    try {
      await axiosInstance.delete(`/lottery/bets/${deleteId}`);
      toast.success("Tiquete eliminado");
      setDeleteId(null);
      fetchToday();
    } catch (e) { toast.error("Error al eliminar"); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card data-testid="bet-form-card">
        <CardHeader>
          <CardTitle className="text-lg">Registrar apuesta</CardTitle>
          <CardDescription>El monto entra a la caja del turno abierto.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {!register && <div className="p-2 rounded bg-red-50 text-red-600 text-sm" data-testid="no-register-warning">No hay turno de caja abierto. Ábrelo para registrar apuestas.</div>}
          <div>
            <Label>Sorteo *</Label>
            <Select value={drawId} onValueChange={(v) => { setDrawId(v); setNumber(""); }}>
              <SelectTrigger data-testid="bet-draw-select"><SelectValue placeholder="Selecciona un sorteo" /></SelectTrigger>
              <SelectContent>
                {activeDraws.map((d) => (
                  <SelectItem key={d.id} value={d.id} data-testid={`bet-draw-${d.id}`}>
                    {d.country} · {d.name} ({d.digits} díg · x{d.payout_normal}) {d.draw_time ? `· ${d.draw_time}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Buscar cliente (código)</Label>
              <div className="flex gap-1">
                <Input value={customerCode} onChange={(e) => setCustomerCode(e.target.value)} placeholder="Ej: 0012" data-testid="bet-customer-code" />
                <Button type="button" variant="outline" onClick={lookupCustomer} data-testid="bet-lookup-btn">Buscar</Button>
              </div>
            </div>
            <div>
              <Label>Jugador *</Label>
              <Input value={playerName} onChange={(e) => setPlayerName(e.target.value)} placeholder="Nombre del jugador" data-testid="bet-player-name" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Números * {draw ? `(${draw.digits} díg c/u)` : ""}</Label>
              <Input
                value={number}
                onChange={(e) => setNumber(formatNumbers(e.target.value))}
                placeholder={draw ? `${"0".repeat(draw.digits)}+${"0".repeat(draw.digits)}...` : "Números"}
                inputMode="numeric"
                data-testid="bet-number"
              />
              {betCount > 1 && <p className="text-xs text-slate-500 mt-1" data-testid="bet-count-hint">{betCount} números · el monto es por cada número</p>}
            </div>
            <div>
              <Label>Monto por número (₡) *</Label>
              <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0" data-testid="bet-amount" />
            </div>
          </div>
          <div className="flex items-center gap-4">
            {draw?.has_serie && (
              <div className="flex-1">
                <Label>Serie</Label>
                <Input value={serie} onChange={(e) => setSerie(e.target.value)} placeholder="Serie" data-testid="bet-serie" />
              </div>
            )}
            {draw?.has_reventado && (
              <label className="flex items-center gap-2 mt-6 cursor-pointer">
                <Checkbox checked={isReventado} onCheckedChange={setIsReventado} data-testid="bet-reventado" />
                <span className="text-sm">Reventado (x{draw.payout_reventado})</span>
              </label>
            )}
          </div>
          {prizePreview > 0 && (
            <div className="p-2 rounded bg-amber-50 text-amber-800 text-sm" data-testid="bet-prize-preview">
              Premio potencial por número: <b>{fmtCRC(prizePreview)}</b>
              {betCount > 1 && <> · Total apostado ({betCount} × {fmtCRC(Number(amount))}): <b>{fmtCRC(totalBet)}</b></>}
            </div>
          )}
          {draw && isDrawClosed(draw, todayCR()) && (
            <div className="p-2 rounded bg-red-50 text-red-600 text-sm" data-testid="bet-closed-warning">
              Apuestas cerradas para {draw.name} (cierra 2 min antes de las {draw.draw_time}).
            </div>
          )}
          <Button className="w-full" onClick={submit} disabled={saving || !register || (draw && isDrawClosed(draw, todayCR()))} data-testid="bet-submit-btn">
            <Plus className="w-4 h-4 mr-1" /> Registrar apuesta
          </Button>
        </CardContent>
      </Card>

      <Card data-testid="today-bets-card">
        <CardHeader>
          <CardTitle className="text-lg">Apuestas de hoy ({todayBets.length})</CardTitle>
          <CardDescription>Total: {fmtCRC(todayBets.reduce((s, b) => s + (b.amount || 0), 0))}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2 max-h-[520px] overflow-y-auto">
          {todayBets.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-6">Sin apuestas hoy</p>
          ) : todayBets.map((b) => (
            <div key={b.id} className="flex items-center justify-between border rounded-lg p-2" data-testid={`bet-row-${b.id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-bold text-lg">{b.number}</span>
                  {b.is_reventado && <Badge variant="secondary">R</Badge>}
                  {b.is_winner && <Badge className="bg-green-500">Ganador</Badge>}
                </div>
                <p className="text-sm text-slate-600 truncate">{b.player_name} · {b.draw_name}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-semibold">{fmtCRC(b.amount)}</span>
                <Button variant="ghost" size="icon" className="text-blue-600" onClick={() => printTicket(b, draws.find((d) => d.id === b.draw_id), box.name)} title="Imprimir tiquete" data-testid={`bet-ticket-${b.id}`}>
                  <Printer className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="icon" className="text-red-500" onClick={() => setDeleteId(b.id)} data-testid={`bet-delete-${b.id}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar tiquete?</AlertDialogTitle>
            <AlertDialogDescription>Se revertirá el ingreso de la caja (y el premio si ya se pagó). Esta acción no se puede deshacer.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction className="bg-red-500 hover:bg-red-600" onClick={doDelete} data-testid="confirm-delete-bet">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function PrizesTab({ box, register, isMaster }) {
  const [data, setData] = useState({ prizes: [], total_prize: 0, total_pending: 0 });
  const [date, setDate] = useState(todayCR());
  const [paying, setPaying] = useState(null);

  const fetchPrizes = useCallback(async () => {
    try {
      const r = await axiosInstance.get(`/lottery/prizes?box_id=${box.id}&date=${date}`);
      setData(r.data);
    } catch (e) { /* noop */ }
  }, [box.id, date]);

  useEffect(() => { fetchPrizes(); }, [fetchPrizes]);

  const pay = async (betId) => {
    setPaying(betId);
    try {
      await axiosInstance.post(`/lottery/prizes/${betId}/pay`, { cash_register_id: register?.id || null });
      toast.success("Premio pagado");
      fetchPrizes();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error al pagar premio"); }
    finally { setPaying(null); }
  };

  return (
    <Card data-testid="prizes-card">
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <CardTitle className="text-lg flex items-center gap-2"><Trophy className="w-5 h-5 text-amber-500" /> Premios del día</CardTitle>
            <CardDescription>Total premios: <b>{fmtCRC(data.total_prize)}</b> · Pendiente: <b className="text-red-600">{fmtCRC(data.total_pending)}</b></CardDescription>
          </div>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-auto" data-testid="prizes-date" />
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {data.prizes.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-8">No hay apuestas ganadoras en esta fecha.</p>
        ) : data.prizes.map((b) => (
          <div key={b.id} className="flex items-center justify-between border rounded-lg p-3" data-testid={`prize-row-${b.id}`}>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-bold text-xl text-green-600">{b.number}</span>
                {b.is_reventado && <Badge variant="secondary">R</Badge>}
                <span className="font-medium">{b.player_name}</span>
              </div>
              <p className="text-sm text-slate-600">{b.draw_name} · Apostó {fmtCRC(b.amount)} → Premio <b>{fmtCRC(b.prize_amount)}</b></p>
            </div>
            {b.prize_paid ? (
              <Badge className="bg-green-500" data-testid={`prize-paid-${b.id}`}>Pagado</Badge>
            ) : isMaster ? (
              <Button size="sm" onClick={() => pay(b.id)} disabled={paying === b.id || !register} data-testid={`prize-pay-${b.id}`}>
                <DollarSign className="w-4 h-4 mr-1" /> Pagar
              </Button>
            ) : (
              <Badge variant="outline">Pendiente</Badge>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function ResultsTab({ box, draws }) {
  const activeDraws = draws.filter((d) => d.active);
  const [drawId, setDrawId] = useState("");
  const [winningNumber, setWinningNumber] = useState("");
  const [serie, setSerie] = useState("");
  const [isReventado, setIsReventado] = useState(false);
  const [date, setDate] = useState(todayCR());
  const [saving, setSaving] = useState(false);
  const [winnerAlert, setWinnerAlert] = useState(null);
  const [results, setResults] = useState([]);
  const draw = draws.find((d) => d.id === drawId);

  const fetchResults = useCallback(async () => {
    try {
      const r = await axiosInstance.get(`/lottery/results?date=${date}`);
      setResults(r.data);
    } catch (e) { /* noop */ }
  }, [date]);
  useEffect(() => { fetchResults(); }, [fetchResults]);

  const submit = async () => {
    if (!drawId) { toast.error("Selecciona un sorteo"); return; }
    if (!winningNumber.trim()) { toast.error("Número ganador requerido"); return; }
    setSaving(true);
    try {
      const r = await axiosInstance.post(`/lottery/results`, {
        draw_id: drawId,
        result_date: date,
        winning_number: winningNumber.trim(),
        is_reventado: isReventado,
        serie: serie.trim() || null,
        box_id: box.id,
      });
      setWinnerAlert(r.data);
      toast.success(`Resultado guardado. ${r.data.winners_count} ganador(es) en esta caja.`);
      setWinningNumber(""); setSerie(""); setIsReventado(false);
      fetchResults();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error al guardar resultado"); }
    finally { setSaving(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card data-testid="results-form-card">
        <CardHeader><CardTitle className="text-lg">Ingresar número ganador</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label>Sorteo *</Label>
            <Select value={drawId} onValueChange={(v) => { setDrawId(v); setWinningNumber(""); }}>
              <SelectTrigger data-testid="result-draw-select"><SelectValue placeholder="Selecciona un sorteo" /></SelectTrigger>
              <SelectContent>
                {activeDraws.map((d) => <SelectItem key={d.id} value={d.id}>{d.country} · {d.name} ({d.digits} díg)</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Número ganador *</Label>
              <Input value={winningNumber} onChange={(e) => setWinningNumber(e.target.value.replace(/\D/g, "").slice(0, draw ? draw.digits : 4))} inputMode="numeric" data-testid="result-number" />
            </div>
            <div>
              <Label>Fecha</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="result-date" />
            </div>
          </div>
          <div className="flex items-center gap-4">
            {draw?.has_serie && (
              <div className="flex-1"><Label>Serie</Label><Input value={serie} onChange={(e) => setSerie(e.target.value)} data-testid="result-serie" /></div>
            )}
            {draw?.has_reventado && (
              <label className="flex items-center gap-2 mt-6 cursor-pointer">
                <Checkbox checked={isReventado} onCheckedChange={setIsReventado} data-testid="result-reventado" />
                <span className="text-sm">Salió reventado</span>
              </label>
            )}
          </div>
          <Button className="w-full" onClick={submit} disabled={saving} data-testid="result-submit-btn"><ClipboardCheck className="w-4 h-4 mr-1" /> Guardar y verificar ganadores</Button>
        </CardContent>
      </Card>

      <Card data-testid="results-list-card">
        <CardHeader><CardTitle className="text-lg">Resultados del {date}</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {results.length === 0 ? <p className="text-sm text-slate-500 text-center py-6">Sin resultados</p> : results.map((r) => (
            <div key={r.id} className="flex items-center justify-between border rounded-lg p-2">
              <span className="text-sm">{r.draw_name}{r.serie ? ` · Serie ${r.serie}` : ""}{r.is_reventado ? " · Reventado" : ""}</span>
              <span className="font-bold text-lg">{r.winning_number}</span>
            </div>
          ))}
        </CardContent>
      </Card>

      <Dialog open={!!winnerAlert} onOpenChange={(o) => !o && setWinnerAlert(null)}>
        <DialogContent data-testid="winner-alert-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><PartyPopper className="w-5 h-5 text-amber-500" /> Verificación de ganadores</DialogTitle>
            <DialogDescription>
              {winnerAlert?.winners_count > 0
                ? `${winnerAlert.winners_count} ganador(es) en esta caja. Total a pagar: ${fmtCRC(winnerAlert.total_prize)}`
                : "No hubo ganadores en esta caja para este resultado."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {(winnerAlert?.winners || []).map((w) => (
              <div key={w.id} className="flex items-center justify-between border rounded-lg p-2 bg-green-50">
                <div>
                  <p className="font-medium">{w.player_name} · <span className="font-bold">{w.number}</span></p>
                  <p className="text-xs text-slate-600">{w.draw_name} · apostó {fmtCRC(w.amount)}</p>
                </div>
                <span className="font-bold text-green-600">{fmtCRC(w.prize_amount)}</span>
              </div>
            ))}
          </div>
          <DialogFooter><Button onClick={() => setWinnerAlert(null)}>Cerrar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function HistoryTab({ box, draws }) {
  const [date, setDate] = useState(todayCR());
  const [drawId, setDrawId] = useState("all");
  const [bets, setBets] = useState([]);

  const fetchBets = useCallback(async () => {
    try {
      let url = `/lottery/bets?box_id=${box.id}&date=${date}`;
      if (drawId && drawId !== "all") url += `&draw_id=${drawId}`;
      const r = await axiosInstance.get(url);
      setBets(r.data);
    } catch (e) { /* noop */ }
  }, [box.id, date, drawId]);
  useEffect(() => { fetchBets(); }, [fetchBets]);

  const totalBet = bets.reduce((s, b) => s + (b.amount || 0), 0);
  const totalPrize = bets.filter((b) => b.is_winner).reduce((s, b) => s + (b.prize_amount || 0), 0);

  return (
    <Card data-testid="history-card">
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-lg">Historial</CardTitle>
          <div className="flex items-center gap-2">
            <Select value={drawId} onValueChange={setDrawId}>
              <SelectTrigger className="w-48" data-testid="history-draw-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los sorteos</SelectItem>
                {draws.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-auto" data-testid="history-date" />
          </div>
        </div>
        <CardDescription>Apuestas: {fmtCRC(totalBet)} · Premios: {fmtCRC(totalPrize)} · Neto: {fmtCRC(totalBet - totalPrize)}</CardDescription>
      </CardHeader>
      <CardContent>
        {bets.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-8">Sin registros para esta fecha.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-slate-500 border-b">
                <tr><th className="p-2">Jugador</th><th className="p-2">Sorteo</th><th className="p-2">N°</th><th className="p-2 text-right">Apostó</th><th className="p-2 text-right">Premio</th><th className="p-2">Estado</th></tr>
              </thead>
              <tbody>
                {bets.map((b) => (
                  <tr key={b.id} className="border-b" data-testid={`history-row-${b.id}`}>
                    <td className="p-2">{b.player_name}</td>
                    <td className="p-2">{b.draw_name}</td>
                    <td className="p-2 font-bold">{b.number}{b.is_reventado ? " R" : ""}</td>
                    <td className="p-2 text-right">{fmtCRC(b.amount)}</td>
                    <td className="p-2 text-right">{b.is_winner ? fmtCRC(b.prize_amount) : "—"}</td>
                    <td className="p-2">
                      {b.is_winner ? (b.prize_paid ? <Badge className="bg-green-500">Pagado</Badge> : <Badge className="bg-amber-500">Ganador</Badge>) : <Badge variant="outline">—</Badge>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ReportTab({ box }) {
  const [date, setDate] = useState(todayCR());
  const [data, setData] = useState({ rows: [], totals: {} });

  const fetchReport = useCallback(async () => {
    try {
      const r = await axiosInstance.get(`/lottery/report?box_id=${box.id}&date=${date}`);
      setData(r.data);
    } catch (e) { /* noop */ }
  }, [box.id, date]);
  useEffect(() => { fetchReport(); }, [fetchReport]);

  const t = data.totals || {};
  return (
    <Card data-testid="report-card">
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <CardTitle className="text-lg flex items-center gap-2"><BarChart3 className="w-5 h-5 text-blue-600" /> Reporte por sorteo</CardTitle>
            <CardDescription>Apostado: <b>{fmtCRC(t.total_amount)}</b> · Premios: <b>{fmtCRC(t.total_prize)}</b> · Pagado: <b>{fmtCRC(t.total_paid)}</b> · Pendiente: <b className="text-red-600">{fmtCRC(t.total_pending)}</b> · Neto: <b className={t.net >= 0 ? "text-green-600" : "text-red-600"}>{fmtCRC(t.net)}</b></CardDescription>
          </div>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-auto" data-testid="report-date" />
        </div>
      </CardHeader>
      <CardContent>
        {(data.rows || []).length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-8">Sin apuestas en esta fecha.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-slate-500 border-b">
                <tr><th className="p-2">Sorteo</th><th className="p-2 text-center">Tiquetes</th><th className="p-2 text-right">Apostado</th><th className="p-2 text-center">Ganadores</th><th className="p-2 text-right">Premios</th><th className="p-2 text-right">Pagado</th><th className="p-2 text-right">Neto</th></tr>
              </thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.draw_id} className="border-b" data-testid={`report-row-${r.draw_id}`}>
                    <td className="p-2 font-medium">{r.draw_name}<span className="text-xs text-slate-400 block">{r.draw_country}</span></td>
                    <td className="p-2 text-center">{r.bets_count}</td>
                    <td className="p-2 text-right">{fmtCRC(r.total_amount)}</td>
                    <td className="p-2 text-center">{r.winners_count}</td>
                    <td className="p-2 text-right">{fmtCRC(r.total_prize)}</td>
                    <td className="p-2 text-right">{fmtCRC(r.total_paid)}</td>
                    <td className={`p-2 text-right font-semibold ${r.net >= 0 ? "text-green-600" : "text-red-600"}`}>{fmtCRC(r.net)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

const emptyDraw = { name: "", country: "Costa Rica", draw_time: "", digits: 2, payout_normal: 90, payout_reventado: 0, max_per_number: 0, has_serie: false, has_reventado: false, active: true };

function DrawsConfig({ draws, onChange }) {
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyDraw);
  const [open, setOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const openNew = () => { setEditing(null); setForm(emptyDraw); setOpen(true); };
  const openEdit = (d) => { setEditing(d); setForm({ ...d }); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Nombre requerido"); return; }
    try {
      const payload = { ...form, digits: Number(form.digits), payout_normal: Number(form.payout_normal), payout_reventado: Number(form.payout_reventado), max_per_number: Number(form.max_per_number) };
      if (editing) await axiosInstance.put(`/lottery/draws/${editing.id}`, payload);
      else await axiosInstance.post(`/lottery/draws`, payload);
      toast.success("Sorteo guardado");
      setOpen(false); onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error al guardar"); }
  };

  const doDelete = async () => {
    try { await axiosInstance.delete(`/lottery/draws/${deleteId}`); toast.success("Sorteo eliminado"); setDeleteId(null); onChange(); }
    catch (e) { toast.error("Error al eliminar"); }
  };

  return (
    <Card data-testid="draws-config-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div><CardTitle className="text-lg">Sorteos y pagos</CardTitle><CardDescription>Configura hora TICA, dígitos y multiplicador de pago por sorteo.</CardDescription></div>
          <Button onClick={openNew} data-testid="draw-new-btn"><Plus className="w-4 h-4 mr-1" /> Nuevo sorteo</Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-slate-500 border-b">
              <tr><th className="p-2">País</th><th className="p-2">Sorteo</th><th className="p-2">Hora</th><th className="p-2">Díg</th><th className="p-2 text-right">Pago</th><th className="p-2 text-right">Reventado</th><th className="p-2">Estado</th><th className="p-2"></th></tr>
            </thead>
            <tbody>
              {draws.map((d) => (
                <tr key={d.id} className="border-b" data-testid={`draw-row-${d.id}`}>
                  <td className="p-2">{d.country}</td>
                  <td className="p-2 font-medium">{d.name}</td>
                  <td className="p-2">{d.draw_time || "—"}</td>
                  <td className="p-2">{d.digits}</td>
                  <td className="p-2 text-right">x{d.payout_normal}</td>
                  <td className="p-2 text-right">{d.payout_reventado ? `x${d.payout_reventado}` : "—"}</td>
                  <td className="p-2">{d.active ? <Badge className="bg-green-500">Activo</Badge> : <Badge variant="outline">Inactivo</Badge>}</td>
                  <td className="p-2 text-right whitespace-nowrap">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(d)} data-testid={`draw-edit-${d.id}`}><Pencil className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="icon" className="text-red-500" onClick={() => setDeleteId(d.id)} data-testid={`draw-delete-${d.id}`}><Trash2 className="w-4 h-4" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="draw-dialog">
          <DialogHeader><DialogTitle>{editing ? "Editar sorteo" : "Nuevo sorteo"}</DialogTitle><DialogDescription>Los cambios de pago aplican a nuevas apuestas.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Nombre *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="draw-name" /></div>
            <div><Label>País</Label><Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} data-testid="draw-country" /></div>
            <div><Label>Hora TICA (HH:MM)</Label><Input value={form.draw_time} onChange={(e) => setForm({ ...form, draw_time: e.target.value })} placeholder="13:00" data-testid="draw-time" /></div>
            <div><Label>Dígitos</Label>
              <Select value={String(form.digits)} onValueChange={(v) => setForm({ ...form, digits: Number(v) })}>
                <SelectTrigger data-testid="draw-digits"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="2">2</SelectItem><SelectItem value="3">3</SelectItem><SelectItem value="4">4</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Pago normal (x)</Label><Input type="number" value={form.payout_normal} onChange={(e) => setForm({ ...form, payout_normal: e.target.value })} data-testid="draw-payout" /></div>
            <div><Label>Pago reventado (x)</Label><Input type="number" value={form.payout_reventado} onChange={(e) => setForm({ ...form, payout_reventado: e.target.value })} data-testid="draw-payout-reventado" /></div>
            <div className="col-span-2"><Label>Tope por número (₡, 0 = sin tope)</Label><Input type="number" value={form.max_per_number} onChange={(e) => setForm({ ...form, max_per_number: e.target.value })} data-testid="draw-max-per-number" /></div>
            <label className="flex items-center gap-2 cursor-pointer"><Checkbox checked={form.has_serie} onCheckedChange={(v) => setForm({ ...form, has_serie: !!v })} data-testid="draw-has-serie" /><span className="text-sm">Usa serie</span></label>
            <label className="flex items-center gap-2 cursor-pointer"><Checkbox checked={form.has_reventado} onCheckedChange={(v) => setForm({ ...form, has_reventado: !!v })} data-testid="draw-has-reventado" /><span className="text-sm">Tiene reventado</span></label>
            <label className="flex items-center gap-2 cursor-pointer col-span-2"><Checkbox checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: !!v })} data-testid="draw-active" /><span className="text-sm">Activo</span></label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} data-testid="draw-save-btn">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>¿Eliminar sorteo?</AlertDialogTitle><AlertDialogDescription>No se podrán registrar nuevas apuestas en este sorteo.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction className="bg-red-500 hover:bg-red-600" onClick={doDelete}>Eliminar</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
